import { useColorMode } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { colors } from '../theme/colors';

export type ColorModeValue = 'light' | 'dark' | 'system';

/**
 * Status bar theme colors for mobile browsers.
 *
 * These colors are derived from the semantic tokens:
 * - Light mode: bg.surface → white
 * - Dark mode: bg.surface → gray.900
 *
 * The theme-color meta tag controls the browser chrome/status bar color on mobile devices.
 */
export const statusBarThemeColors = {
	/** Light mode status bar color (matches bg.surface in light mode) */
	light: colors.white.replace(/ff$/, ''), // Remove alpha suffix for meta tag
	/** Dark mode status bar color (matches bg.surface in dark mode) */
	dark: colors.gray['900'].replace(/ff$/, ''), // Remove alpha suffix for meta tag
} as const;

/**
 * Update the theme-color meta tag for mobile browser status bar.
 *
 * This should be called whenever the color mode changes to keep the
 * browser chrome in sync with the app theme.
 */
export const updateThemeColorMeta = (mode: 'light' | 'dark') => {
	if (typeof document === 'undefined') return;
	const meta = document.querySelector('meta[name="theme-color"]');
	if (meta) {
		meta.setAttribute('content', statusBarThemeColors[mode]);
	}
};

/**
 * Our own storage key for tracking the user's preference (including 'system').
 * This is separate from Chakra's 'chakra-ui-color-mode' key because Chakra
 * only stores resolved values ('light' or 'dark') and would overwrite 'system'.
 */
const USER_PREFERENCE_STORAGE_KEY = 'bonusly-color-mode-preference';

/**
 * Get the system's color scheme preference
 */
const getSystemColorMode = (): 'light' | 'dark' => {
	if (typeof window === 'undefined') return 'light';
	return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

/**
 * Get the stored color mode preference from localStorage
 * Falls back to Chakra's key for migration from pre-existing dark mode users
 */
const getStoredPreference = (): ColorModeValue => {
	if (typeof window === 'undefined') return 'light';

	// Check our new preference key first
	const stored = localStorage.getItem(USER_PREFERENCE_STORAGE_KEY);
	if (stored === 'system' || stored === 'light' || stored === 'dark') {
		return stored;
	}

	// Migration: check Chakra's key for existing dark mode users
	// This preserves their preference after the switch to the new key
	const chakraStored = localStorage.getItem('chakra-ui-color-mode');
	if (chakraStored === 'dark') {
		// Migrate their preference to our new key
		localStorage.setItem(USER_PREFERENCE_STORAGE_KEY, 'dark');
		return 'dark';
	}

	return 'light';
};

/**
 * Hook to access and control the user's color mode preference
 *
 * Returns the current color mode and functions to change it.
 *
 * Note: Color mode enforcement is handled by SeasonalThemeProvider to avoid
 * conflicts with seasonal theme dark mode forcing.
 *
 * Supports three modes:
 * - 'light': Always use light theme
 * - 'dark': Always use dark theme
 * - 'system': Follow the user's system preference (dynamically updates when OS preference changes)
 */
export const useUserColorMode = () => {
	const chakraColorMode = useColorMode();

	// Destructure with fallbacks for test environments where ChakraProvider may not be set up
	const colorMode = chakraColorMode?.colorMode ?? 'light';
	const toggleColorMode = chakraColorMode?.toggleColorMode;
	const setChakraColorMode = chakraColorMode?.setColorMode;

	// Track the user's preference (including 'system') separately from Chakra's resolved mode
	// Start with 'light' - we'll sync with stored preference once company data is loaded
	const [userPreference, setUserPreference] = useState<ColorModeValue>('light');

	// Track if we've done the initial sync (to avoid re-running on every render)
	const hasInitializedRef = useRef(false);

	// Custom setColorMode that handles 'system' preference properly
	const setColorMode = useCallback(
		(mode: ColorModeValue) => {
			setUserPreference(mode);
			localStorage.setItem(USER_PREFERENCE_STORAGE_KEY, mode);

			if (mode === 'system') {
				// When setting to 'system', resolve to current OS preference
				const resolvedMode = getSystemColorMode();
				setChakraColorMode?.(resolvedMode);
				updateThemeColorMeta(resolvedMode);
			} else {
				setChakraColorMode?.(mode);
				updateThemeColorMeta(mode);
			}
		},
		[setChakraColorMode]
	);

	// Sync color mode on initial mount
	useEffect(() => {
		// Only sync once on mount
		if (hasInitializedRef.current) return;
		hasInitializedRef.current = true;

		const stored = getStoredPreference();
		setUserPreference(stored);

		// Always sync Chakra's color mode with our stored preference.
		// This is important because Chakra has its own localStorage key (chakra-ui-color-mode)
		// that might have a different value (e.g., 'dark' from before this feature).
		if (stored === 'system') {
			const resolvedMode = getSystemColorMode();
			setChakraColorMode?.(resolvedMode);
			updateThemeColorMeta(resolvedMode);
		} else {
			setChakraColorMode?.(stored);
			updateThemeColorMeta(stored);
		}
	}, [setChakraColorMode]);

	// Listen for OS color scheme changes when preference is 'system'
	useEffect(() => {
		// Don't listen if user isn't in system mode
		if (userPreference !== 'system') return;

		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

		const handleChange = (e: MediaQueryListEvent) => {
			const resolvedMode = e.matches ? 'dark' : 'light';
			setChakraColorMode?.(resolvedMode);
			updateThemeColorMeta(resolvedMode);
		};

		// Modern browsers
		mediaQuery.addEventListener('change', handleChange);

		return () => {
			mediaQuery.removeEventListener('change', handleChange);
		};
	}, [userPreference, setChakraColorMode]);

	return {
		/** Current resolved color mode ('light' | 'dark') - what's actually being displayed */
		colorMode,
		/** True if currently displaying dark mode */
		isDarkMode: colorMode === 'dark',
		/** True if user can change their color mode preference */
		canChangeColorMode: true,
		/** Function to toggle between light and dark mode */
		toggleColorMode,
		/** Function to set specific color mode ('light', 'dark', or 'system') */
		setColorMode,
		/** The user's stored preference ('light', 'dark', or 'system') - not the resolved value */
		userPreference,
	};
};
